import React, { FC, ReactElement, useContext, useState } from 'react'
import { Form } from 'react-final-form'
import { Box, Button } from '@mui/material'
import Grid from '@mui/material/Grid'
import { errorMapper } from 'src/i18n/ErrorMapper'
import { useTranslate } from 'src/i18n/useMessageSource'
import {
  Configuration,
  LaundryUser,
  LaundryUsersPrepaidApi,
  ManualPrepaidAdjustment,
  PrepaidBalance,
} from 'src/service/backend/api'
import { HttpContext } from 'src/service/backend/http/HttpContext'
import {
  LaundryUserTopupViewModel,
  mapLaundryUserTopupViewModelToManualPrepaidAdjustment,
} from 'src/service/view-model/laundry-user/LaundryUserTopupViewModel'
import { ErrorMessage } from 'src/ui-shared/base/error-message/ErrorMessage'
import { DetailsTextField } from 'src/ui-shared/base/form/control/DetailsTextField'
import { TextField } from 'src/ui-shared/base/form/control/TextFieldValidate'
import {
  composeValidators,
  required,
  validAmount,
  validNumberRange,
} from 'src/ui-shared/base/form/validation/Validators'
import { LoadingIndicator } from 'src/ui-shared/base/loading-indicator/LoadingIndicator'
import { useShowSnackbar } from 'src/ui-shared/base/snackbar/SnackbarProvider'
import { useSharedStyles } from 'src/ui-shared/constants/Shared.style'

interface Props {
  laundryUser: LaundryUser
  balance: PrepaidBalance
  setOpenModal: (value: boolean) => void
  loadBalance: () => void
}

export const LaundryUserPaymentManualAdjustment: FC<Props> = ({
  laundryUser,
  balance,
  setOpenModal,
  loadBalance,
}): ReactElement => {
  const translate = useTranslate()
  const { classes: sharedClasses } = useSharedStyles()
  const showSnackbar = useShowSnackbar()

  const httpConfiguration: Configuration = useContext(HttpContext)
  const laundryUserPrepaidApi = new LaundryUsersPrepaidApi(httpConfiguration)

  const [loading, setLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const assignedLaundryGroups = laundryUser.assignedLaundryGroups
  const defaultViewModel: LaundryUserTopupViewModel = {
    amount: undefined,
    currency: balance.currency,
    laundryGroupRef: assignedLaundryGroups.length > 0 ? laundryUser.assignedLaundryGroups[0] : undefined,
  }
  const maxAmount = 200

  // handle events
  const submitForm = (viewModel: LaundryUserTopupViewModel) => {
    const manualPrepaidAdjustment: ManualPrepaidAdjustment =
      mapLaundryUserTopupViewModelToManualPrepaidAdjustment(viewModel)
    setErrorMessage(null)
    setLoading(true)

    laundryUserPrepaidApi
      .laundryusersLaundryUserIdPrepaidAdjustmentPost(laundryUser.id, manualPrepaidAdjustment)
      .then(() => {
        setOpenModal(false)
        setLoading(false)
        const message = translate('manualPrepaidAdjustmentSuccessful')
        showSnackbar(message, 'success')
        loadBalance()
      })
      .catch((err) => {
        const errorMessage = errorMapper(err, translate)
        console.error(errorMessage, err)
        setErrorMessage(errorMessage)
        setLoading(false)
      })
  }

  return (
    <Box
      sx={{
        px: 3,
        pb: 3,
      }}
    >
      <LoadingIndicator loading={loading} />
      {errorMessage ? <ErrorMessage message={errorMessage} /> : null}
      <Box
        sx={{
          mt: 2,
        }}
      >
        <Form<LaundryUserTopupViewModel>
          initialValues={defaultViewModel}
          onSubmit={submitForm}
          render={({ handleSubmit, submitting, pristine }) => {
            return (
              <form onSubmit={handleSubmit} autoComplete="off">
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      name="amount"
                      label={translate('balance')}
                      fullWidth
                      autoFocus
                      validate={composeValidators(
                        required(),
                        validAmount('validation.valid.amount'),
                        validNumberRange(0, maxAmount, 'validation.valid.amountMax'),
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <DetailsTextField value={defaultViewModel.currency} label={translate('currency')} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField name="reason" label={translate('reason')} fullWidth multiline validate={required()} />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    variant="text"
                    color="primary"
                    size="large"
                    className={sharedClasses.ButtonMargin}
                    onClick={() => setOpenModal(false)}
                  >
                    {translate('button.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    type="submit"
                    disabled={pristine || submitting}
                  >
                    {translate('button.adjust')}
                  </Button>
                </Box>
              </form>
            )
          }}
        />
      </Box>
    </Box>
  )
}
